/**
 * "YB / No YB" — did the driver clearly say they collected the customer's
 * return bag? (SPEC-BagCollectionYB, docs/specs/bag-collection/)
 *
 * On CookUnity drop-offs drivers type YB (bag collected) or NB (no bag) into
 * the free-text Onfleet completion note. This file reads that note and gives
 * one of two answers:
 *
 *   "yb"      the note has a clear, known "bag collected" signal
 *   "not_yb"  anything else: NB, typos ("yd"), unknown codes, blank notes
 *
 * The rule of thumb (Sarah, 2026-09-23): when in doubt, say No YB. A Senior
 * checking a delivery that was fine costs little; skipping one where the bag
 * was missed leads to a customer complaint.
 *
 * The rules run top to bottom and the first match wins. Every "no" rule comes
 * before every "yes" rule, so a note that says both counts as No YB.
 *
 * Pure: no database, no network, no clock. Shared so the server's Bag filter
 * and the list's pill tooltip can never disagree.
 */

export type BagCollection = "yb" | "not_yb";

export type BagRuleId =
  | "empty"
  | "says_no"
  | "yb_zero"
  | "yb_code"
  | "rp_count"
  | "rp_zero"
  | "rb_count"
  | "rb_zero"
  | "pickup_count"
  | "sentence"
  | `driver_${string}`
  | "no_yes_signal";

export interface BagCollectionResult {
  value: BagCollection;
  rule: BagRuleId;
}

/** The two filter values, in the order the list shows them. */
export const BAG_COLLECTION_VALUES: readonly BagCollection[] = ["not_yb", "yb"];

// ─────────────────────────────────────────────────────────────────────────────
// Driver-specific codes — "learning to speak driver".
//
// Some codes are used by only one driver. Each row says: for a driver whose
// name contains `driverNameIncludes`, a note matching `pattern` means YB.
// To add a code: confirm what it means with Sarah first, then add ONE row here
// and ONE test in shared/__tests__/bag-collection.test.ts.
// ─────────────────────────────────────────────────────────────────────────────

interface DriverCode {
  driverNameIncludes: string; // lowercase
  rule: `driver_${string}`;
  pattern: RegExp;            // tested against the normalised (lowercase) note
  description: string;        // tooltip text
}

const DRIVER_CODES: DriverCode[] = [
  {
    // Norman writes "1 bag", "2 bags", "1 bag / h2h" to mean he collected them.
    // Other drivers use "3 bags" to describe what they DELIVERED, so this is
    // Norman-only, and the note must START with the count.
    driverNameIncludes: "norman villeneuve",
    rule: "driver_norman",
    pattern: /^[1-9]\d* bags?\b/,
    description: "Norman's code: '1 bag' = bag collected",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Tooltip text for each rule.
// ─────────────────────────────────────────────────────────────────────────────

const RULE_DESCRIPTIONS: Record<string, string> = {
  empty:         "No driver note",
  says_no:       "Driver wrote NB / no bag",
  yb_zero:       "Driver wrote YB with a count of 0",
  yb_code:       "Driver wrote YB",
  rp_count:      "Driver code: 1rp (return package)",
  rp_zero:       "Driver code: 0rp (no return package)",
  rb_count:      "Driver code: 1 RB (return bag)",
  rb_zero:       "Driver code: 0 RB (no return bag)",
  pickup_count:  "Driver wrote how many bags were picked up",
  sentence:      "Driver described collecting a bag",
  no_yes_signal: "No clear YB in the driver's note",
};
for (const code of DRIVER_CODES) RULE_DESCRIPTIONS[code.rule] = code.description;

/** Human description of a rule, for tooltips. */
export function describeBagRule(rule: BagRuleId | string): string {
  return RULE_DESCRIPTIONS[rule] ?? "No clear YB in the driver's note";
}

// ─────────────────────────────────────────────────────────────────────────────
// The classifier
// ─────────────────────────────────────────────────────────────────────────────

const yes = (rule: BagRuleId): BagCollectionResult => ({ value: "yb", rule });
const no = (rule: BagRuleId): BagCollectionResult => ({ value: "not_yb", rule });

/**
 * Read a CookUnity completion note (task_data.completionDetails.notes) and say
 * whether it clearly reports a collected return bag.
 */
export function classifyBagCollection(
  note: string | null | undefined,
  driverName?: string | null,
): BagCollectionResult {
  // Normalise: lowercase, every run of whitespace (incl. newlines) → one space.
  const text = (note ?? "").toLowerCase().replace(/\s+/g, " ").trim();

  // 1. empty — no note at all.
  if (text === "") return no("empty");

  // 2. says_no — "nb" that does not follow a letter (letters may follow it, so
  //    "NBand" counts), or "no bag(s)", "no delivery/empty/return bag(s)",
  //    "zero bags". Checked before every "yes" rule: a note that says both is No YB.
  if (/(?<![a-z])nb/.test(text)) return no("says_no");
  if (/\bno (?:delivery |empty |return )?bags?\b/.test(text)) return no("says_no");
  if (/\bzero bags?\b/.test(text)) return no("says_no");
  //    A negated or questioned YB is a "no" too: "no yb", "not yb",
  //    "yb not collected", "yb none", "yb n/a", "yb?".
  if (/\bnot? y[1-9]?b(?![a-z])/.test(text)) return no("says_no");
  if (/(?<![a-z])y[1-9]?b ?(?:\?|(?:not|none|n\/?a)\b)/.test(text)) return no("says_no");

  // 3. yb_zero — a YB code with a count of 0, however it is written:
  //    "yb 0", "yb-0", "yb - 0", "yb: 0", "yb=0", "yb (0)", "yb — 0", "0 yb".
  if (/(?<![a-z])y[1-9]?b(?![a-z])[^a-z0-9]{0,4}0+(?!\d)/.test(text)) return no("yb_zero");
  if (/(?<![a-z0-9.])0+ ?y[1-9]?b(?![a-z])/.test(text)) return no("yb_zero");

  // 4. yb_code — "yb", or y + one digit 1–9 + b ("y2b"), with no letter
  //    directly before or after it. "yd", "yp", "y", "yes" never count.
  if (/(?<![a-z])y[1-9]?b(?![a-z])/.test(text)) return yes("yb_code");

  // 5. rp_count — a number then "rp" (return package): "at door 1rp". 0 = none.
  const rp = text.match(/(?<![a-z0-9])(\d+) ?rp(?![a-z])/);
  if (rp) return Number(rp[1]) >= 1 ? yes("rp_count") : no("rp_zero");

  // 6. rb_count — a number then "rb" (return bag): "1 RB". 0 = none.
  const rb = text.match(/(?<![a-z0-9])(\d+) ?rb(?![a-z])/);
  if (rb) return Number(rb[1]) >= 1 ? yes("rb_count") : no("rb_zero");

  // Rules 7–9 read counts and sentences, so they need extra care: a negation
  // ("couldn't pick up 1 bag") or the CUSTOMER doing the picking up
  // ("customer picked up 2 bags") is not the driver collecting a bag.
  // When either shows up, skip rules 7–9 and fall through to No YB.
  const negated = /\b(?:not|never|unable|cannot|can'?t|couldn'?t|didn'?t|wasn'?t|weren'?t)\b/.test(text);
  const byCustomer = /\bcustomer (?:picked|pick|took|collected|grabbed|retrieved)\b|\bby (?:the )?customer\b/.test(text);
  if (negated || byCustomer) return no("no_yes_signal");

  // 7. pickup_count — "pickup / pick up N bag(s)", or
  //    "N bag(s) picked up / collected / retrieved", with N of 1 or more.
  const pickupBefore = text.match(/\bpick ?up (\d+) bags?\b/);
  if (pickupBefore && Number(pickupBefore[1]) >= 1) return yes("pickup_count");
  const pickupAfter = text.match(/(?<![a-z0-9])(\d+) bags? (?:(?:was|were) )?(?:picked up|collected|retrieved)\b/);
  if (pickupAfter && Number(pickupAfter[1]) >= 1) return yes("pickup_count");

  // 8. sentence — a spelled-out collection sentence:
  //    "one/two/three/four/a single … bag(s) was/were retrieved/picked up/collected"
  //    "picked up one/two/a … bag"
  //    Up to two words may sit between the count and "bag" ("one delivery bag").
  if (/\b(?:one|two|three|four|a single)(?: [a-z]+){0,2} bags? (?:was|were) (?:retrieved|picked up|collected)\b/.test(text)) {
    return yes("sentence");
  }
  if (/\bpicked up (?:one|two|three|four|a)(?: [a-z]+){0,2} bags?\b/.test(text)) {
    return yes("sentence");
  }

  // 9. driver codes — codes that only one driver uses (table above).
  const driver = (driverName ?? "").toLowerCase();
  for (const code of DRIVER_CODES) {
    if (driver.includes(code.driverNameIncludes) && code.pattern.test(text)) return yes(code.rule);
  }

  // 10. no_yes_signal — anything else is No YB.
  return no("no_yes_signal");
}
